import { createPilotEvent } from "./pilotEventSchema";

const VALID_VERSIONS = new Set(["v1", "v2"]);
const VALID_READINESS = new Set(["candidate", "parity-approved"]);
const CODE_PATTERN = /^[a-z0-9][a-z0-9._-]{0,119}$/i;

const frozenDecision = (version, reason) => Object.freeze({ version, reason });
const fallback = (reason) => frozenDecision("v1", reason);

const isNonEmptyString = (value) => (
  typeof value === "string" && value.trim() !== ""
);

const includesDestination = (enabledDestinations, destinationId) => {
  if (enabledDestinations instanceof Set) {
    return enabledDestinations.has(destinationId);
  }
  return Array.isArray(enabledDestinations) && enabledDestinations.includes(destinationId);
};

const destinationIsDisabled = (rollout, destinationId) => {
  if (!Object.prototype.hasOwnProperty.call(rollout, "disabledDestinations")) {
    return false;
  }
  const disabled = rollout.disabledDestinations;
  const values = disabled instanceof Set
    ? [...disabled]
    : Array.isArray(disabled)
      ? disabled
      : null;
  if (!values) return true;
  if (
    values.some((value) => !isNonEmptyString(value) || !CODE_PATTERN.test(value)) ||
    new Set(values).size !== values.length
  ) return true;
  return values.includes(destinationId);
};

const cohortAllowsDestination = (rollout, destinationId) => {
  if (!Object.prototype.hasOwnProperty.call(rollout, "destinationCohorts")) {
    return rollout.cohortEligible === true;
  }
  if (!Array.isArray(rollout.destinationCohorts)) return false;
  const seen = new Set();
  const valid = rollout.destinationCohorts.every((entry) => {
    if (
      !entry ||
      typeof entry !== "object" ||
      typeof entry.destinationId !== "string" ||
      !CODE_PATTERN.test(entry.destinationId) ||
      seen.has(entry.destinationId) ||
      typeof entry.cohortEligible !== "boolean"
    ) return false;
    seen.add(entry.destinationId);
    return true;
  });
  if (!valid) return false;
  const matches = rollout.destinationCohorts.filter(
    (entry) => entry.destinationId === destinationId
  );
  if (matches.length > 1) return false;
  return matches.length === 1
    ? matches[0].cohortEligible === true
    : rollout.cohortEligible === true;
};

const resolveDesiredVersion = (requestedVersion, preferredVersion) => {
  if (requestedVersion === "v1" || requestedVersion === "v2") {
    return requestedVersion;
  }
  if (requestedVersion !== undefined && requestedVersion !== "auto") return null;
  return VALID_VERSIONS.has(preferredVersion) ? preferredVersion : "v1";
};

const decide = ({
  destinationId,
  preferredVersion = "v1",
  readiness,
  requestedVersion = "auto",
  rollout,
} = {}) => {
  const desiredVersion = resolveDesiredVersion(requestedVersion, preferredVersion);
  if (!desiredVersion) return fallback("invalid-version");
  if (desiredVersion === "v1") return frozenDecision("v1", "preferred");

  if (!isNonEmptyString(destinationId)) {
    return fallback("invalid-destination");
  }
  if (!rollout || rollout.globalV2Enabled !== true) {
    return fallback("global-kill-switch");
  }
  if (!cohortAllowsDestination(rollout, destinationId)) {
    return fallback("cohort-disabled");
  }
  if (destinationIsDisabled(rollout, destinationId)) {
    return fallback("destination-disabled");
  }

  if (
    VALID_READINESS.has(readiness) &&
    rollout.previewCandidates === true
  ) {
    return frozenDecision("v2", "development-preview");
  }

  if (!includesDestination(rollout.enabledDestinations, destinationId)) {
    return fallback("destination-disabled");
  }
  if (readiness === undefined || readiness === null) return fallback("v2-missing");
  if (!VALID_READINESS.has(readiness)) return fallback("invalid-readiness");
  if (readiness !== "parity-approved") {
    return fallback("v2-not-approved");
  }

  return frozenDecision("v2", "preferred");
};

const validateDependencies = (telemetry, now) => {
  if (!telemetry || typeof telemetry.append !== "function") {
    throw new TypeError("telemetry.append must be a function");
  }
  if (typeof now !== "function") throw new TypeError("now must be a function");
};

/**
 * Policy and observability seam for a progressive V1/V2 pilot.
 *
 * `decide` is synchronous and fail-closed. `record` validates a privacy-safe
 * event envelope and never lets telemetry availability block the product.
 */
export const createPilotControl = ({ telemetry, now = Date.now }) => {
  validateDependencies(telemetry, now);

  const record = async (input) => {
    let event;
    try {
      event = createPilotEvent(input, now());
    } catch (_) {
      return Object.freeze({ status: "dropped", reason: "invalid-event" });
    }

    try {
      const appended = await telemetry.append(event);
      if (appended?.status === "dropped") {
        return Object.freeze({
          status: "dropped",
          reason: appended.reason || "telemetry",
        });
      }
      return Object.freeze({ status: "recorded", event });
    } catch (_) {
      return Object.freeze({ status: "dropped", reason: "telemetry" });
    }
  };

  return Object.freeze({ decide, record });
};
